/**
 * Frontend utility for filtering and grouping Optum data
 */

type JsonMap = Record<string, any>
export const OPTUM_FILTER_VERSION_FIELD = '__optumFilterVersion'
export const OPTUM_FILTER_VERSION = 1
export const OPTUM_FIELD_LABELS: Record<string, string> = {
    payorIdentification: 'Payor Identification',
    postalCode: 'Postal Code',
    address1: 'Address 1',
    address2: 'Address 2',
    contactName: 'Contact Name',
    telephone: 'Telephone',
    fax: 'Fax',
    email: 'Email',
    url: 'Uniform Resource Locator (URL)',
    benefitsRelatedEntities: 'Benefits Related Entities',
    entityIdentification: 'Entity Identification',
    entityIdentificationValue: 'Entity Identification Value',
    dateOfDeath: 'Date of Death',
}

interface OptumBenefitsInformation {
    name?: string
    serviceTypes?: string[]
    [key: string]: any
}

interface OptumApiResponse {
    benefitsInformation?: OptumBenefitsInformation[]
    [key: string]: any
}

type GroupedBenefitsInformation = {
    serviceType?: string
    serviceTypeCodes?: string[]
    benefits?: JsonMap[]
    benefitsArray?: JsonMap[]
}

const TOP_LEVEL_FIELDS_TO_REMOVE = <const>[
    'x12',
    'meta',
    'provider',
    'planStatus',
    'controlNumber',
    'reassociationKey',
    'tradingPartnerServiceId',
    'subscriberTraceNumbers',
]

const TARGET_SERVICE_TYPES = <const>['Health Benefit Plan Coverage', 'Diagnostic Medical', 'Diagnostic X-Ray']

const normalize = (value: string | undefined | null): string => {
    if (!value) return ''
    return value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()
}

const BENEFIT_FIELDS_TO_KEEP = <const>[
    'name',
    'planCoverage',
    'insuranceType',
    'insuranceTypeCode',
    'benefitAmount',
    'benefitPercent',
    'coverageLevel',
    'coverageLevelCode',
    'timeQualifier',
    'inPlanNetworkIndicator',
    'inPlanNetworkIndicatorCode',
    'description',
    'plan',
    'benefit',
    'service',
    'eligibility',
    'planBegin',
    'planEnd',
    'eligibilityBegin',
    'eligibilityEnd',
    'effectiveDateOfChange',
    'dateOfLastUpdate',
    'coordinationOfBenefits',
    'groupNumber',
    'insurancePolicyNumber',
]


const isDefined = <T>(value: T | undefined | null): value is T => value !== undefined && value !== null

const pickDefined = <T extends JsonMap>(source: JsonMap | undefined, keys: readonly string[]): T | undefined => {
    if (!source || typeof source !== 'object') return undefined

    const picked: JsonMap = {}
    let hasValues = false

    for (const key of keys) {
        const value = source[key]
        if (value !== undefined && value !== null && value !== '') {
            picked[key] = value
            hasValues = true
        }
    }

    return hasValues ? <T>picked : undefined
}

const isEmpty = (value: unknown): boolean =>
    value === undefined
    || value === null
    || value === ''
    || (Array.isArray(value) && !value.length)
    || (typeof value === 'object' && !Array.isArray(value) && value !== null && !Object.keys(value).length)

const removeEmptyValues = <T extends JsonMap>(obj: T): void => {
    Object.keys(obj).forEach((key) => {
        if (isEmpty(obj[key])) delete obj[key]
    })
}

const flattenDescriptionList = (items: unknown[] | undefined): string | undefined => {
    if (!Array.isArray(items)) return undefined

    const descriptions = items
        .map((item: unknown) => {
            if (item && typeof item === 'object' && 'description' in item) {
                return String((<{ description?: unknown }>item).description || '').trim()
            }
            return ''
        })
        .filter(Boolean)

    return descriptions.length ? [...new Set(descriptions)].join(' | ') : undefined
}

const sanitizeAddress = (address: JsonMap | undefined): JsonMap | undefined =>
    pickDefined(address, ['address1', 'address2', 'city', 'state', 'postalCode'])

const sanitizeContactFields = (contactInformation: JsonMap | undefined): JsonMap | undefined => {
    if (!contactInformation || typeof contactInformation !== 'object') return undefined

    const contacts = Array.isArray(contactInformation.contacts) ? contactInformation.contacts : []
    const contactValues: Record<string, string[]> = {}

    for (const contact of contacts) {
        if (!contact || typeof contact !== 'object') continue
        const contactObj = <Record<string, unknown>>contact
        const mode = normalize(typeof contactObj.communicationMode === 'string' ? contactObj.communicationMode : undefined)
        const value = contactObj.communicationNumber
        if (!mode || !value) continue
        if (!contactValues[mode]) contactValues[mode] = []
        contactValues[mode].push(String(value))
    }

    return pickDefined<JsonMap>({
        contactName: contactInformation.name,
        telephone: contactValues[normalize('Telephone')]?.join(' | '),
        fax: contactValues[normalize('Fax')]?.join(' | '),
        email: contactValues[normalize('Email')]?.join(' | '),
        url: contactValues[normalize('Uniform Resource Locator (URL)')]?.join(' | '),
    }, ['contactName', 'telephone', 'fax', 'email', 'url'])
}

const sanitizeBenefitDates = (value: JsonMap | undefined): JsonMap | undefined =>
  pickDefined(value, [
    'plan',
    'benefit',
    'service',
    'eligibility',
    'planBegin',
    'planEnd',
    'eligibilityBegin',
    'eligibilityEnd',
    'effectiveDateOfChange',
    'dateOfLastUpdate',
    'coordinationOfBenefits',
    'dateOfDeath',
  ])

// Sanitizes a single related entity object, preserving all identification fields,
// address, and contact info as a flat structure.
const sanitizeRelatedEntity = (entity: unknown): JsonMap | undefined => {
    if (!entity || typeof entity !== 'object') return undefined
    const e = <JsonMap>entity

    const sanitized = pickDefined<JsonMap>(e, [
        'entityName',
        'entityType',
        'entityIdentifier',
        'entityIdentification',
        'entityIdentificationValue',
    ])
    if (!sanitized) return undefined

    if (e.address && typeof e.address === 'object') {
        const address = sanitizeAddress(<JsonMap>e.address)
        if (address) Object.assign(sanitized, address)
    }

    if (e.contactInformation && typeof e.contactInformation === 'object') {
        const contact = sanitizeContactFields(<JsonMap>e.contactInformation)
        if (contact) Object.assign(sanitized, contact)
    }

    removeEmptyValues(sanitized)
    return Object.keys(sanitized).length ? sanitized : undefined
}

const sanitizeBenefit = (benefit: JsonMap): JsonMap | undefined => {
    const sanitized = pickDefined<JsonMap>(benefit, BENEFIT_FIELDS_TO_KEEP)
    if (!sanitized) return undefined

    if (Array.isArray(benefit.additionalInformation)) {
        const description = flattenDescriptionList(benefit.additionalInformation)
        if (description) sanitized.description = description
    }

    if (benefit.benefitsDateInformation && typeof benefit.benefitsDateInformation === 'object') {
      const dates = sanitizeBenefitDates(<JsonMap>benefit.benefitsDateInformation)
      if (dates) Object.assign(sanitized, dates)
    }

    if (benefit.benefitsAdditionalInformation && typeof benefit.benefitsAdditionalInformation === 'object') {
        sanitized.benefitsAdditionalInformation = benefit.benefitsAdditionalInformation
    }

    // Collect all related entities into a sanitized array
    const rawEntities: unknown[] = Array.isArray(benefit.benefitsRelatedEntities)
        ? benefit.benefitsRelatedEntities
        : benefit.benefitsRelatedEntity
        ? [benefit.benefitsRelatedEntity]
        : []

    if (rawEntities.length > 0) {
        const entities = rawEntities.map(sanitizeRelatedEntity).filter(isDefined)
        if (entities.length) sanitized.benefitsRelatedEntities = entities
    }

    removeEmptyValues(sanitized)
    if (!Object.keys(sanitized).length) return undefined

    // Drop benefits that carry no meaningful content beyond basic type identifiers
    const IDENTITY_ONLY_KEYS = new Set(['name', 'insuranceType', 'insuranceTypeCode', 'planCoverage'])
    const hasMeaningfulContent = Object.keys(sanitized).some((k) => !IDENTITY_ONLY_KEYS.has(k))
    if (!hasMeaningfulContent) return undefined

    return sanitized
}

const isGroupedBenefitsInformation = (benefitsInformation: unknown[]): benefitsInformation is GroupedBenefitsInformation[] =>
    benefitsInformation.length > 0
    && typeof benefitsInformation[0] === 'object'
    && benefitsInformation[0] !== null
    && ('benefits' in <JsonMap>benefitsInformation[0] || 'benefitsArray' in <JsonMap>benefitsInformation[0])

const sanitizeGroupedBenefits = (benefitsInformation: GroupedBenefitsInformation[]): GroupedBenefitsInformation[] =>
    benefitsInformation
        .filter((group) => TARGET_SERVICE_TYPES.some((st) => normalize(group.serviceType) === normalize(st)))
        .map((group) => ({
            serviceType: group.serviceType,
            serviceTypeCodes: Array.isArray(group.serviceTypeCodes) && group.serviceTypeCodes.length
                ? group.serviceTypeCodes
                : undefined,
            benefits: (group.benefits || group.benefitsArray || [])
                .map((benefit) => sanitizeBenefit(benefit))
                .filter(isDefined)
                .sort((a, b) => (<string>a?.name || '').localeCompare(<string>b?.name || '')),
        }))
        .filter((group) => group.benefits && group.benefits.length > 0)

const sanitizeRawBenefits = (benefitsInformation: OptumBenefitsInformation[]): GroupedBenefitsInformation[] => {
    const groups: Record<string, JsonMap[]> = {}

    for (const benefit of benefitsInformation) {
        const serviceTypes = benefit.serviceTypes || []

        if (serviceTypes.length === 0) {
            // Benefits with no serviceTypes are placed under Health Benefit Plan Coverage
            const hbc = 'Health Benefit Plan Coverage'
            if (!groups[hbc]) groups[hbc] = []
            const sanitized = sanitizeBenefit(benefit)
            if (sanitized) groups[hbc].push(sanitized)
            continue
        }

        for (const serviceType of TARGET_SERVICE_TYPES) {
            const matchesGroup = serviceTypes.some((item) => normalize(item) === normalize(serviceType))
            if (!matchesGroup) continue

            if (!groups[serviceType]) groups[serviceType] = []
            const sanitized = sanitizeBenefit(benefit)
            if (sanitized) groups[serviceType].push(sanitized)
        }
    }

    return Object.entries(groups)
        .map(([serviceType, benefits]) => ({
            serviceType,
            benefits: benefits.sort((a, b) => (<string>a?.name || '').localeCompare(<string>b?.name || '')),
        }))
        .filter((group) => group.benefits.length > 0)
}

const sanitizePlanInformation = (planInformation: JsonMap | undefined): JsonMap | undefined => pickDefined(planInformation, [
    'caseNumber', 'planNumber', 'groupNumber',
    'planDescription', 'groupDescription',
    'planNetworkIdNumber', 'planNetworkIdDescription',
])

const flattenPayerContactInformation = (payer: JsonMap): JsonMap => {
    const contactInformation = payer.contactInformation
    if (!contactInformation || typeof contactInformation !== 'object') return payer

    const ci = <JsonMap>contactInformation
    const contacts = Array.isArray(ci.contacts) ? ci.contacts : []

    const flat: JsonMap = { ...ci }
    delete flat.contacts

    for (const contact of contacts) {
        if (contact && typeof contact === 'object') {
            Object.assign(flat, contact)
        }
    }

    return { ...payer, contactInformation: Object.keys(flat).length ? flat : undefined }
}

export const getOptumFieldLabel = (key: string): string => {
    if (OPTUM_FIELD_LABELS[key]) return OPTUM_FIELD_LABELS[key]
    const spaced = key.replace(/([A-Z])/g, ' $1').trim()
    return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

export const filterOptumResponse = (rawResponse: OptumApiResponse): OptumApiResponse => {
    if (!rawResponse) return rawResponse

    const filteredResponse: OptumApiResponse = { ...rawResponse }
    filteredResponse[OPTUM_FILTER_VERSION_FIELD] = OPTUM_FILTER_VERSION

    for (const field of TOP_LEVEL_FIELDS_TO_REMOVE) {
        delete filteredResponse[field]
    }

    if (filteredResponse.payer && typeof filteredResponse.payer === 'object') {
        filteredResponse.payer = flattenPayerContactInformation(<JsonMap>filteredResponse.payer)
    }

    if (filteredResponse.planInformation && typeof filteredResponse.planInformation === 'object') {
        filteredResponse.planInformation = sanitizePlanInformation(<JsonMap>filteredResponse.planInformation)
    }

    if (filteredResponse.planDateInformation && typeof filteredResponse.planDateInformation === 'object') {
        filteredResponse.planDateInformation = sanitizeBenefitDates(<JsonMap>filteredResponse.planDateInformation)
    }

    if (Array.isArray(filteredResponse.benefitsInformation)) {
        filteredResponse.benefitsInformation = isGroupedBenefitsInformation(filteredResponse.benefitsInformation)
            ? sanitizeGroupedBenefits(filteredResponse.benefitsInformation)
            : sanitizeRawBenefits(<OptumBenefitsInformation[]>filteredResponse.benefitsInformation)
    }

    removeEmptyValues(filteredResponse)

    return filteredResponse
}
